/**
 * Unit base statistics
 * Ported from Godot Unit.gd HULL_STATS
 */

import { UnitType, type UnitStats } from '../types/GameTypes';

/**
 * Shield Rule:
 *   shields ≈ HP × 0.5 × (1 + defense / 20)
 *
 *   - Ships with 0 defense get ~50% of HP as shields (light buffer).
 *   - Each point of defense increases shields proportionally, so
 *     heavily armored ships (Cruiser) have larger shield pools.
 *   - Utility ships (Space Tug) get minimal shields.
 *
 * Final values are hand-tuned for gameplay balance.
 */
export const UNIT_STATS: Record<UnitType, UnitStats> = {
  [UnitType.SCOUT]: {
    hp: 50,
    shields: 25,
    attack: 5,
    defense: 0,
    speed: 5,
    attackRange: 1,
    componentSlots: 2,
    cost: 50,
    cargoCapacity: 100,
    miningEfficiency: 0.5,
  },
  [UnitType.FIGHTER]: {
    hp: 100,
    shields: 40,
    attack: 15,
    defense: 0,
    speed: 3,
    attackRange: 1,
    componentSlots: 3,
    cost: 100,
    cargoCapacity: 200,
    miningEfficiency: 0.5,
  },
  [UnitType.CRUISER]: {
    hp: 300,
    shields: 120,
    attack: 40,
    defense: 0,
    speed: 2,
    attackRange: 2,
    componentSlots: 5,
    cost: 200,
    cargoCapacity: 1000,
    miningEfficiency: 1.0,
  },
  [UnitType.SPACE_TUG]: {
    hp: 50,
    shields: 15,
    attack: 0,
    defense: 0,
    speed: 2,
    attackRange: 0,
    componentSlots: 6,
    cost: 75,
    cargoCapacity: 5000,
    miningEfficiency: 2.0,
  },
  [UnitType.ALIEN_LEVIATHAN]: {
    hp: 5000,
    shields: 2500,
    attack: 200,
    defense: 50,
    speed: 1,
    attackRange: 5,
    componentSlots: 0,
    cost: 0,
    cargoCapacity: 0,
    miningEfficiency: 0,
  },
};

// Unit costs extracted from UNIT_STATS for convenience
export const UNIT_COSTS: Record<UnitType, number> = {
  [UnitType.SCOUT]: UNIT_STATS[UnitType.SCOUT].cost,
  [UnitType.FIGHTER]: UNIT_STATS[UnitType.FIGHTER].cost,
  [UnitType.CRUISER]: UNIT_STATS[UnitType.CRUISER].cost,
  [UnitType.SPACE_TUG]: UNIT_STATS[UnitType.SPACE_TUG].cost,
  [UnitType.ALIEN_LEVIATHAN]: UNIT_STATS[UnitType.ALIEN_LEVIATHAN].cost,
};

export const BUILDING_COSTS: Record<number, number> = {
  1: 100, // RESEARCH_CENTER
  2: 150, // SHIPYARD
  3: 120, // DEFENSE_PLATFORM
  4: 80, // POWER_PLANT
  5: 60, // SENSOR_ARRAY
};

// Re-export shared constants from common
export {
  BASE_ACCURACY,
  MAX_EVASION,
  DEFENSE_PLATFORM_DAMAGE_REDUCTION,
  STARTING_CREDITS,
  BASE_PLANET_PRODUCTION,
  POWER_PLANT_BONUS,
  MINING_FACILITY_COSTS,
} from '../common/constants';

// Client-only mining constants
export const MINING_PRODUCTION = {
  level1: 5,
  level2: 10,
  level3: 15,
};

export const MINING_STORAGE = {
  level1: 50,
  level2: 100,
  level3: 150,
};

// Phase timing (milliseconds)
export const DEFAULT_PHASE_DURATION = 5 * 60 * 1000; // 5 minutes
